import React from 'react';
import { Navigate } from 'react-router-dom';
import { Box, CircularProgress, Typography, Alert } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useImpersonation } from '../contexts/ImpersonationContext';
import { hasStoreProductQaAccess } from '../utils/productQaAccess';

interface ProductQaGuardProps {
  children: React.ReactNode;
}

/**
 * Restricts the store Product QA queue to users who were explicitly granted
 * `productQaEnabled`. Motzu admins use `/app/admin/staged-products` instead;
 * impersonating a store must not inherit that access.
 */
export const ProductQaGuard: React.FC<ProductQaGuardProps> = ({ children }) => {
  const { currentUser, userProfile, loading } = useAuth();
  const { isImpersonating } = useImpersonation();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  const hasAccess = hasStoreProductQaAccess({
    isImpersonating,
    productQaEnabled: userProfile?.productQaEnabled,
  });

  if (!hasAccess) {
    return (
      <Box p={3}>
        <Alert severity="error">
          <Typography variant="h6" gutterBottom>
            Access Denied
          </Typography>
          <Typography>
            You don't have permission to access Product QA. A Motzu admin has
            to grant this access explicitly.
          </Typography>
        </Alert>
      </Box>
    );
  }

  return <>{children}</>;
};
